import { useEffect } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { RivalsLeaguesHeader } from '@sorare/core/src/components/landing/RivalsLeaguesHeader';
import { AppLayout } from '@sorare/core/src/components/navigation/AppLayout';
import { FOOTBALL_PLAY_RIVALS_LEAGUES } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import useIsLoggedIn from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import useLoggedCallback from '@sorare/core/src/hooks/useLoggedCallback';
import MultiSportFooter from '@sorare/core/src/routing/MultiSportFooter';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Footer } from 'RivalsLeagues/Footer';

import coop from './assets/coop.png';
import fantasy from './assets/fantasy.png';
import field from './assets/field.svg';
import lock from './assets/lock.svg';
import mobileCoop from './assets/mobile-coop.png';
import mobileExperience from './assets/mobile-experience.png';
import mobileZidane from './assets/mobile-zidane.png';
import up from './assets/up.svg';
import write from './assets/write.svg';
import zz from './assets/zz.png';

const messages = defineMessages({
  metadatasTitle: {
    id: 'rivals-leagues.landing.meta.title',
    defaultMessage:
      'Play Sorare Rivals Leagues for the upcoming European Championship. Be the best to play with Zidane',
  },
  metadatasDescription: {
    id: 'rivals-leagues.landing.meta.description',
    defaultMessage:
      'Create or join a Rivals League for the European Championship, compete with your friends against others to win 350,000+ rewards and more',
  },
});

const Grid = styled.main`
  display: grid;
  gap: calc(10 * var(--unit));
  padding: 10vh 0;
  position: relative;
  @media ${laptopAndAbove} {
    gap: var(--double-unit);
    grid-template-columns: 1fr 1fr;
  }
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: #ad00ff;
    mix-blend-mode: difference;
    opacity: 0.1;
    filter: blur(161.218px);
    border-radius: 2000px;
    z-index: -1;
  }
`;

const Block = styled.article`
  @media ${laptopAndAbove} {
    margin: auto;
    max-width: 360px;
  }
`;

const Placeholder = styled.div`
  display: none;
  @media ${laptopAndAbove} {
    display: block;
    position: relative;
    border-radius: 2em;
    background: rgba(var(--c-static-rgb-neutral-500), 0.1);
    width: 100%;
    height: 740px;
  }
`;
const Illu = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;
const MobileIllu = styled.img`
  width: 100vw;
  max-width: 500px;
  margin: 0 calc(var(--container-padding) * -1);
  @media ${laptopAndAbove} {
    display: none;
  }
`;
const Icon = styled.img`
  position: absolute;
  right: 100%;
  transform: translateX(50%);
  &.field {
    top: 100%;
    transform: translate(50%, -50%);
  }
  &.up {
    top: 65%;
  }
  &.write {
    top: 15%;
  }
  &.lock {
    right: 0;
    top: 75%;
  }
`;
const Title = styled.h1`
  font-size: clamp(var(--fs-32), 8vw, 64px); /** 80 / 1920 * 100 = 3.33 */
  line-height: 1;
  font-weight: normal;
`;
type Props = {
  fromLanding?: boolean;
};
const RivalsLeagues = ({ fromLanding }: Props) => {
  const { setPageMetadata } = useSeoContext();
  const { formatMessage } = useIntl();

  const {
    flags: { enableEuro2024 = false, useRivalsLeagueLanding = false },
  } = useFeatureFlags();
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();
  const playNow = useLoggedCallback(
    () => {
      navigate(FOOTBALL_PLAY_RIVALS_LEAGUES);
    },
    true,
    true
  );

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: 'https://frontend-assets.sorare.com/meta/rivals-leagues.jpg',
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  if (!enableEuro2024) {
    return null;
  }

  if (useRivalsLeagueLanding && isLoggedIn && !fromLanding) {
    return <Navigate to={FOOTBALL_PLAY_RIVALS_LEAGUES} />;
  }

  return (
    <AppLayout>
      <RivalsLeaguesHeader onCreate={playNow} />
      <Container>
        <Grid>
          <Block>
            <Vertical gap={4}>
              <Vertical>
                <BodyL uppercase bold>
                  <FormattedMessage
                    id="rivals-leagues.block1.suptitle"
                    defaultMessage="Exclusive Rewards"
                  />
                </BodyL>
                <Title>
                  <FormattedMessage
                    id="rivals-leagues.block1.title"
                    defaultMessage="Play football with Zidane"
                  />
                </Title>
                <BodyL>
                  <FormattedMessage
                    id="rivals-leagues.block1.description"
                    defaultMessage="Win VIP match day tickets, signed jerseys and much more. Finish top with your League and you and your friends will play football with Zidane."
                  />
                </BodyL>
              </Vertical>
              <footer>
                <Button color="secondary" size="medium" onClick={playNow}>
                  <FormattedMessage
                    id="rivals-leagues.block1.cta"
                    defaultMessage="Play Rivals Leagues"
                  />
                </Button>
              </footer>
              <MobileIllu src={mobileZidane} alt="" />
            </Vertical>
          </Block>
          <Placeholder>
            <Illu src={zz} alt="" width="924px" height="784px" />
            <Icon className="field" src={field} alt="" />
          </Placeholder>
          <Placeholder>
            <Illu src={fantasy} alt="" width="450px" height="549px" />
            <Icon className="up" src={up} alt="" />
          </Placeholder>
          <Block>
            <Vertical gap={4}>
              <Vertical>
                <BodyL uppercase bold>
                  <FormattedMessage
                    id="rivals-leagues.block2.suptitle"
                    defaultMessage="Experience Tactical freedom"
                  />
                </BodyL>
                <Title>
                  <FormattedMessage
                    id="rivals-leagues.block2.title"
                    defaultMessage="Fantasy Football for Real Fans"
                  />
                </Title>
                <BodyL>
                  <FormattedMessage
                    id="rivals-leagues.block2.description"
                    defaultMessage="Set your lineups and tactics around your favourite games each match day. Track live scores with our sophisticated scoring system. Yes, defensive midfielders finally get the appreciation they deserve!"
                  />
                </BodyL>
              </Vertical>
              <footer>
                <Button color="secondary" size="medium" onClick={playNow}>
                  <FormattedMessage
                    id="rivals-leagues.block2.cta"
                    defaultMessage="Create my Rivals League"
                  />
                </Button>
              </footer>
              <MobileIllu src={mobileExperience} alt="" />
            </Vertical>
          </Block>
          <Block>
            <Vertical gap={4}>
              <Vertical>
                <BodyL uppercase bold>
                  <FormattedMessage
                    id="rivals-leagues.block3.suptitle"
                    defaultMessage="Co-OP GAMEPLAY "
                  />
                </BodyL>
                <Title>
                  <FormattedMessage
                    id="rivals-leagues.block3.title"
                    defaultMessage="Prove your League knows best"
                  />
                </Title>
                <BodyL>
                  <FormattedMessage
                    id="rivals-leagues.block3.description"
                    defaultMessage="Clash against other leagues on the global leaderboard. You and your friend’s fantasy football lineup scores count towards your Rivals League ranking."
                  />
                </BodyL>
              </Vertical>
              <footer>
                <Button color="secondary" size="medium" onClick={playNow}>
                  <FormattedMessage
                    id="rivals-leagues.block3.cta"
                    defaultMessage="Create my Rivals League"
                  />
                </Button>
              </footer>
              <MobileIllu src={mobileCoop} alt="" />
            </Vertical>
          </Block>
          <Placeholder>
            <Illu src={coop} alt="" width="375px" height="346px" />
            <Icon className="write" src={write} alt="" />
            <Icon className="lock" src={lock} alt="" />
          </Placeholder>
        </Grid>
      </Container>
      <Footer onCreate={playNow} />
      <MultiSportFooter />
    </AppLayout>
  );
};

export default RivalsLeagues;
